/**
 * Event builder utilities for note telemetry
 * Provides standardized builders that ensure consistent payload structure
 */

import type {
  EditEventMetrics,
  NoteViewedMetrics,
  SectionEventMetrics,
  SelectionMetrics,
} from './noteTelemetry';
import { trackNoteTelemetry } from './noteTelemetry';
import { validateAndCoerceNoteEvent } from './noteTelemetrySchema';

export type TelemetryContext = {
  docId: string;
  getDocVersion: () => string;
};

export type SelectionCopiedParams = {
  context: TelemetryContext;
  lenSelected: number;
  lenTotal: number;
  modality: 'copy_button' | 'copy_all' | 'copy_selected';
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type SelectionSettledParams = {
  context: TelemetryContext;
  lenSelected: number;
  lenTotal: number;
  timings: {
    startedAt: number;
    lastChangedAt: number;
    settledAt: number;
  };
  modality?: 'mouse' | 'touch' | 'keyboard';
  isBackward?: boolean;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type SectionRenderedParams = {
  context: TelemetryContext;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type SectionInteractedParams = {
  context: TelemetryContext;
  hoverDuration: number;
  interactionCounts?: {
    clicks?: number;
    selects?: number;
    copies?: number;
  };
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type NoteViewedParams = {
  context: TelemetryContext;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

/**
 * Build and emit a text_copied event for button-triggered copies
 */
export function buildSelectionCopied(params: SelectionCopiedParams): void {
  const now = performance.now();
  const payload: Partial<SelectionMetrics> = {
    selectionId: crypto.randomUUID(),
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    chars: {
      selected: params.lenSelected,
      total: params.lenTotal,
      percent: params.lenTotal > 0 ? params.lenSelected / params.lenTotal : 0,
    },
    timings: {
      startedAt: now,
      lastChangedAt: now,
      copiedAt: now,
    },
    modality: params.modality,
    ua: navigator.userAgent,
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('text_copied', payload) as SelectionMetrics;
  trackNoteTelemetry('text_copied', validated);
}

/**
 * Build and emit a text_selected event when selection settles
 */
export function buildSelectionSettled(params: SelectionSettledParams): void {
  const msToSettle = params.timings.settledAt - params.timings.startedAt;
  const payload: Partial<SelectionMetrics> = {
    selectionId: crypto.randomUUID(),
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    chars: {
      selected: params.lenSelected,
      total: params.lenTotal,
      percent: params.lenTotal > 0 ? params.lenSelected / params.lenTotal : 0,
    },
    timings: {
      startedAt: params.timings.startedAt,
      lastChangedAt: params.timings.lastChangedAt,
      settledAt: params.timings.settledAt,
      msToSettle,
    },
    modality: params.modality,
    isBackward: params.isBackward,
    ua: navigator.userAgent,
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('text_selected', payload) as SelectionMetrics;
  trackNoteTelemetry('text_selected', validated);
}

/**
 * Build and emit a section_rendered event
 */
export function buildSectionRendered(params: SectionRenderedParams): void {
  const payload: Partial<SectionEventMetrics> = {
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('section_rendered', payload) as SectionEventMetrics;
  trackNoteTelemetry('section_rendered', validated);
}

/**
 * Build and emit a section_interacted event
 */
export function buildSectionInteracted(params: SectionInteractedParams): void {
  const payload: Partial<SectionEventMetrics> = {
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    hoverDuration: params.hoverDuration,
    interactionCounts: params.interactionCounts,
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('section_interacted', payload) as SectionEventMetrics;
  trackNoteTelemetry('section_interacted', validated);
}

/**
 * Build and emit a note_viewed event
 */
export function buildNoteViewed(params: NoteViewedParams): void {
  const payload: Partial<NoteViewedMetrics> = {
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('note_viewed', payload) as NoteViewedMetrics;
  trackNoteTelemetry('note_viewed', validated);
}

export type SectionEditedParams = {
  context: TelemetryContext;
  beforeLen: number;
  afterLen: number;
  source: 'manual' | 'ai_edit' | 'regeneration';
  timings?: {
    startedAt?: number;
  };
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

/**
 * Build and emit a section_edited event when a section is saved
 */
export function buildSectionEdited(params: SectionEditedParams): void {
  const savedAt = performance.now();
  const charsDelta = params.afterLen - params.beforeLen;
  const percentDelta =
    params.beforeLen > 0 ? Math.abs(charsDelta) / params.beforeLen : (charsDelta !== 0 ? 1 : 0);
  const msInEdit =
    params.timings?.startedAt !== undefined ? Math.max(0, savedAt - params.timings.startedAt) : undefined;

  const payload: Partial<EditEventMetrics> = {
    docId: params.context.docId,
    docVersion: params.context.getDocVersion(),
    edit: {
      charsBefore: params.beforeLen,
      charsAfter: params.afterLen,
      charsDelta,
      percentDelta,
    },
    timings: {
      ...(params.timings?.startedAt !== undefined && { startedAt: params.timings.startedAt }),
      savedAt,
      ...(msInEdit !== undefined && { msInEdit }),
    },
    source: params.source,
    metadata: params.metadata,
  };

  const validated = validateAndCoerceNoteEvent('section_edited', payload) as EditEventMetrics;
  trackNoteTelemetry('section_edited', validated);
}

